'use client';
import { jsx } from '@emotion/react/jsx-runtime';
import { useRef, useState, useCallback } from 'react';
import { createPolymorphicComponent } from '@s7/base';
import { useId, useClickOutside } from '@s7/hooks';
import { useProps, useTheme } from '@s7/styles';
import { isNumber } from '@s7/utils';
import { PopoverContextProvider } from './Popover.context.js';
import { IDENT, defaultProps } from './Popover.contract.js';
import { usePopover } from './usePopover.js';
import { PopoverTarget } from './PopoverTarget/PopoverTarget.js';
import { PopoverDropdown } from './PopoverDropdown/PopoverDropdown.js';
import { getFloatingPosition } from '../Floating/getFloatingPosition/getFloatingPosition.js';

function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const _Popover = (props)=>{
    var _popover_floating_middlewareData_arrow, _popover_floating_middlewareData, _popover_floating, _popover_floating_middlewareData_arrow1, _popover_floating_middlewareData1, _popover_floating1;
    const _useProps = useProps(IDENT, props, defaultProps), [{ children, position, offset, onPositionChange, positionDependencies, middlewares, opened, defaultOpened, onClose, onOpen, onChange, transitionProps, width, radius, shadow, id, zIndex, disabled, keepMounted, withArrow, arrowSize, arrowOffset, arrowRadius, arrowPosition, trapFocus, returnFocus, closeOnClickOutside, closeOnEscape, clickOutsideEvents, withinPortal, portalProps, withRoles, preset }] = _useProps, others = _object_without_properties_loose(_useProps[0], [
        "children",
        "position",
        "offset",
        "onPositionChange",
        "positionDependencies",
        "middlewares",
        "opened",
        "defaultOpened",
        "onClose",
        "onOpen",
        "onChange",
        "transitionProps",
        "width",
        "radius",
        "shadow",
        "id",
        "zIndex",
        "disabled",
        "keepMounted",
        "withArrow",
        "arrowSize",
        "arrowOffset",
        "arrowRadius",
        "arrowPosition",
        "trapFocus",
        "returnFocus",
        "closeOnClickOutside",
        "closeOnEscape",
        "clickOutsideEvents",
        "withinPortal",
        "portalProps",
        "withRoles",
        "preset"
    ]);
    const arrowRef = useRef(null);
    const [targetNode, setTargetNode] = useState(null);
    const [dropdownNode, setDropdownNode] = useState(null);
    const uid = useId(id);
    const theme = useTheme();
    const computedOffset = isNumber(offset) ? offset + (withArrow ? arrowSize / 2 : 0) : offset;
    const computedPosition = getFloatingPosition(theme.dir, position);
    const popover = usePopover({
        middlewares,
        width,
        position: computedPosition,
        offset: computedOffset,
        arrowRef,
        arrowOffset,
        onPositionChange,
        positionDependencies,
        opened,
        defaultOpened,
        onChange,
        onOpen,
        onClose
    });
    useClickOutside(()=>{
        if (popover.opened && closeOnClickOutside) {
            popover.onClose();
        }
    }, clickOutsideEvents, [
        targetNode,
        dropdownNode
    ]);
    const reference = useCallback((node)=>{
        /*
             * В данном случае не работаем с виртуальными элементами,
             * поэтому можем быть уверены, что node это HTMLElement.
             * */ setTargetNode(node);
        popover.floating.refs.setReference(node);
    }, [
        popover.floating.refs.setReference
    ]);
    const floating = useCallback((node)=>{
        setDropdownNode(node);
        popover.floating.refs.setFloating(node);
    }, [
        popover.floating.refs.setFloating
    ]);
    return /*#__PURE__*/ jsx(PopoverContextProvider, {
        value: {
            returnFocus,
            disabled,
            controlled: popover.controlled,
            reference,
            floating,
            x: popover.floating.x,
            y: popover.floating.y,
            arrowX: (_popover_floating = popover.floating) == null ? void 0 : (_popover_floating_middlewareData = _popover_floating.middlewareData) == null ? void 0 : (_popover_floating_middlewareData_arrow = _popover_floating_middlewareData.arrow) == null ? void 0 : _popover_floating_middlewareData_arrow.x,
            arrowY: (_popover_floating1 = popover.floating) == null ? void 0 : (_popover_floating_middlewareData1 = _popover_floating1.middlewareData) == null ? void 0 : (_popover_floating_middlewareData_arrow1 = _popover_floating_middlewareData1.arrow) == null ? void 0 : _popover_floating_middlewareData_arrow1.y,
            opened: popover.opened,
            arrowRef,
            transitionProps,
            width,
            withArrow,
            arrowSize,
            arrowOffset,
            arrowRadius,
            arrowPosition,
            placement: popover.floating.placement,
            trapFocus,
            withinPortal,
            portalProps,
            zIndex,
            radius,
            preset,
            shadow,
            closeOnEscape,
            onClose: popover.onClose,
            onToggle: popover.onToggle,
            getTargetId: ()=>`${uid}-target`,
            getDropdownId: ()=>`${uid}-dropdown`,
            withRoles,
            targetProps: others,
            keepMounted
        },
        children: children
    });
};
const Popover = createPolymorphicComponent(_Popover);
Popover.displayName = IDENT;
Popover.Target = PopoverTarget;
Popover.Dropdown = PopoverDropdown;
var Popover$1 = Popover;

export { Popover, Popover$1 as default };
//# sourceMappingURL=Popover.js.map
